import { BreadcrumbJsonLd } from 'next-seo';
import PropTypes from 'prop-types';

import SchemaJsonLd from 'components/Seo/SchemaJsonLd';

import { getStoreImageUrl } from 'lib/image';
import {
  toAbsoluteLink,
  toStoreCouponsLink,
  toStoreOffersLink,
} from 'lib/links';
import placeholder from 'lib/placeholder';

import APP from 'constants/app';
import LINKS from 'constants/links';

const WebPageSchema = ({ serverDate, storeImage, storeName, storeSlug }) => {
  const pageUrl = toAbsoluteLink(toStoreCouponsLink(storeSlug));
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: storeName,
    url: pageUrl,
    lastReviewed: serverDate,
  };

  if (storeImage && !storeImage.startsWith(APP.LOCAL_IMAGE_PATH)) {
    schema.primaryImageOfPage = {
      '@type': 'ImageObject',
      url: getStoreImageUrl(storeImage, 400),
    };
  }

  return <SchemaJsonLd id="webpage-schema" schema={schema} />;
};

const StoreJsonLd = ({
  storeDomain,
  storeImage,
  storeName,
  storeRating,
  storeRatingCount,
  storeSlug,
}) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Store',
    name: storeName,
    url: storeDomain
      ? `https://${storeDomain}`
      : toAbsoluteLink(toStoreCouponsLink(storeSlug)),
  };

  if (storeImage && !storeImage.startsWith(APP.LOCAL_IMAGE_PATH)) {
    schema.image = getStoreImageUrl(storeImage, 400);
  }

  if (storeRating && storeRatingCount > 0) {
    schema.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: storeRating,
      ratingCount: storeRatingCount,
      bestRating: 5,
      worstRating: 1,
    };
  }

  return <SchemaJsonLd id="store-schema" schema={schema} />;
};

const StoreSchema = ({
  isOfferPage,
  serverDate,
  storeDomain,
  storeImage,
  storeName,
  storeRating,
  storeRatingCount,
  storeSlug,
}) => (
  <>
    <BreadcrumbJsonLd
      itemListElements={[
        {
          position: 1,
          name: placeholder('breadcrumbs.app'),
          item: APP.URL,
        },
        {
          position: 2,
          name: isOfferPage
            ? placeholder('breadcrumbs.stores')
            : placeholder('breadcrumbs.coupons'),
          item: toAbsoluteLink(LINKS.STORES),
        },
        {
          position: 3,
          name: storeName,
          item: isOfferPage
            ? toAbsoluteLink(toStoreOffersLink(storeSlug))
            : toAbsoluteLink(toStoreCouponsLink(storeSlug)),
        },
      ]}
    />
    {!isOfferPage && (
      <>
        <WebPageSchema
          serverDate={serverDate}
          storeImage={storeImage}
          storeName={storeName}
          storeSlug={storeSlug}
        />
        <StoreJsonLd
          storeDomain={storeDomain}
          storeImage={storeImage}
          storeName={storeName}
          storeRating={storeRating}
          storeRatingCount={storeRatingCount}
          storeSlug={storeSlug}
        />
      </>
    )}
  </>
);

StoreSchema.propTypes = {
  isOfferPage: PropTypes.bool.isRequired,
  serverDate: PropTypes.string,
  storeDomain: PropTypes.string,
  storeImage: PropTypes.string,
  storeName: PropTypes.string.isRequired,
  storeRating: PropTypes.number,
  storeRatingCount: PropTypes.number,
  storeSlug: PropTypes.string.isRequired,
};

export default StoreSchema;
